import { useMutation, useQueryClient } from 'react-query';
import { expDetailsTemplateKeys } from '../api/exp-details-template-key-factory';
import { ExperimentTemplateData } from '@experiment-details/types';
import useExpTemplateList from './useExpTemplateList';
import { getNewNameCount } from '@shared';
import {
  loadExpDetailsTemplate,
  newExpDetailsTemplate,
  removeExpDetailsTemplate
} from '../api/expDetailsTemplateApi';
import useExpDetailsRouteParams from '../useExpDetailsRouteParams';
import chartApi from '@/util/experimentDetails/chartApi';
import { cloneDeep, noop } from 'lodash';

type PrefetchDuplicateTemplateData = {
  templateId: string;
  personalTemplateId?: string;
  formData: {
    name?: string;
    isDefault?: boolean;
  };
};

type DuplicateTemplateData = {
  templateData: ExperimentTemplateData;
  personalTemplateId?: string;
  formData?: {
    name?: string;
    isDefault?: boolean;
  };
};

const shouldPrefetchTemplate = (
  data: PrefetchDuplicateTemplateData | DuplicateTemplateData
): data is PrefetchDuplicateTemplateData => 'templateId' in data;

type DuplicateTemplateArgs = {
  data: PrefetchDuplicateTemplateData | DuplicateTemplateData;
  existedTemplateNames: string[];
  projectId: string;
  experimentKey: string;
};
const duplicateTemplate = async ({
  data,
  existedTemplateNames,
  projectId,
  experimentKey
}: DuplicateTemplateArgs) => {
  const shouldPrefetch = shouldPrefetchTemplate(data);

  let templateData: ExperimentTemplateData;

  if (shouldPrefetch) {
    templateData = await loadExpDetailsTemplate({
      templateId: data.templateId,
      projectId
    });
  } else {
    templateData = cloneDeep(data.templateData);
  }

  if (data.formData?.name) {
    templateData.chartTemplate.template_name = data.formData.name;
  } else {
    const clonesCount = getNewNameCount(
      existedTemplateNames,
      `${templateData.templateName} (Copy`
    );
    const duplicateName = `${templateData.templateName} ${
      clonesCount ? `(Copy ${clonesCount})` : '(Copy)'
    }`;

    templateData.chartTemplate.template_name = duplicateName;
  }

  templateData.projectId = projectId;

  const res = await newExpDetailsTemplate(templateData, experimentKey);

  if (data.formData?.isDefault) {
    await chartApi.setProjectTemplate({
      project_id: res.data.projectId,
      template_id: res.data.templateId
    });
  }

  if (data.personalTemplateId) {
    await removeExpDetailsTemplate({
      projectId,
      templateId: data.personalTemplateId
    }).catch(noop);
  }

  return res.data;
};

export default function useDuplicateExpDetailsTemplateMutation() {
  const queryClient = useQueryClient();
  const { projectId, experimentKey } = useExpDetailsRouteParams();
  const { data: templateList = [] } = useExpTemplateList();
  const existedTemplateNames = templateList.map(
    template => template.templateName
  );

  return useMutation(
    (data: PrefetchDuplicateTemplateData | DuplicateTemplateData) =>
      duplicateTemplate({
        data,
        existedTemplateNames,
        projectId,
        experimentKey
      }),
    {
      onSuccess: async (template, { personalTemplateId }) => {
        await queryClient.invalidateQueries(
          expDetailsTemplateKeys.templateList(projectId),
          {
            refetchInactive: true
          }
        );

        // refetch after deletion
        if (personalTemplateId) {
          await queryClient.invalidateQueries(
            expDetailsTemplateKeys.personalTemplate(experimentKey, projectId)
          );
        }
      }
    }
  );
}
